import { createTheme, darken, lighten } from "@mui/material";
import { KeyboardArrowDown } from "@mui/icons-material";

declare module "@mui/material/styles" {
  interface TypeText {
    tertiary: string;
  }
  interface TypographyVariants {
    display: React.CSSProperties;
    body3: React.CSSProperties;
  }

  interface TypographyVariantsOptions {
    display?: React.CSSProperties;
    body3?: React.CSSProperties;
  }
}

declare module "@mui/material/Typography" {
  interface TypographyPropsVariantOverrides {
    display: true;
    body3: true;
  }
  interface TypographyPropsColorOverrides {
    tertiary: true;
  }
}

export const getCustomTheme = (
  primaryColor: string,
  secondaryColor: string,
  textColor: string,
  secondaryTextColor: string,
) => {
  const lightSecondary = lighten(secondaryColor, 0.6);
  const darkSecondary = darken(secondaryColor, 0.05);
  const theme = createTheme({
    shape: {
      borderRadius: 12,
    },
    typography: {
      fontFamily: "Inter, sans-serif",
      fontWeightLight: 400,
      fontWeightRegular: 500,
      fontWeightMedium: 600,
      fontWeightBold: 700,
      display: {
        fontWeight: 700,
        fontSize: 32,
        lineHeight: "120%",
      },
      h1: {
        fontWeight: 600,
        fontSize: 24,
        lineHeight: "130%",
      },
      h2: {
        fontWeight: 600,
        fontSize: 20,
        lineHeight: "140%",
      },
      h3: {
        fontWeight: 600,
        fontSize: 18,
        lineHeight: "140%",
      },
      subtitle1: {
        fontWeight: 400,
        fontSize: 16,
        lineHeight: "160%",
      },
      body1: {
        fontWeight: 400,
        fontSize: 14,
        lineHeight: "160%",
      },
      body2: {
        fontWeight: 400,
        fontSize: 12,
        lineHeight: "150%",
      },
      body3: {
        fontWeight: 400,
        fontSize: 11,
        lineHeight: "140%",
      },
    },
    palette: {
      primary: {
        main: primaryColor,
      },
      secondary: {
        main: secondaryColor,
        light: lightSecondary,
        dark: darkSecondary,
      },
      text: {
        primary: textColor,
        secondary: secondaryTextColor,
      },
      divider: "#e0e3eb",
    },
    components: {
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            boxSizing: "border-box",
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: "#E0E3EB",
              borderRadius: "8px",
            },
            "& .MuiSvgIcon-root": {
              color: secondaryTextColor,
              fontSize: "20px",
            },
          },
          input: {
            height: "46px",
            boxSizing: "border-box",
            fontWeight: 500,
            fontSize: "14px",
            "&::placeholder": {
              fontWeight: 400,
              fontSize: "14px",
              color: secondaryTextColor,
              opacity: 1,
            },
          },
        },
      },
      MuiSelect: {
        styleOverrides: {
          outlined: {
            padding: "12px 16px",
            height: "46px",
            display: "flex",
            alignItems: "center",
            boxSizing: "border-box",
          },
        },
        defaultProps: {
          IconComponent: KeyboardArrowDown,
        },
      },
      MuiAutocomplete: {
        styleOverrides: {
          paper: {
            fontSize: "14px",
            fontWeight: 500,
          },
          root: {
            "& .MuiInputBase-root": {
              height: "46px",
              "& input": {
                height: "100%",
              },
            },
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: "capitalize",
            fontSize: "14px",
            height: "38px",
            padding: "8px 20px",
            fontWeight: 600,
            borderRadius: "12px",
            boxShadow:
              "0 1px 2px rgba(0, 0, 0, 0.05), 0 1px 3px rgba(0, 0, 0, 0.1)",
            "&:hover": {
              boxShadow:
                "0 2px 4px rgba(0, 0, 0, 0.1), 0 2px 6px rgba(0, 0, 0, 0.15)",
            },
          },
        },
      },
      MuiSvgIcon: {
        defaultProps: {
          fontSize: "small",
        },
      },

      MuiMenu: {
        styleOverrides: {
          paper: {
            borderRadius: "12px",
          },
        },
      },
    },
  });
  return theme;
};
